// package
import { Fragment, useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import {
  Button,
  Card,
  Col,
  Row,
  OverlayTrigger,
  Tooltip,
  Form,
  Image,
} from "react-bootstrap";
import AmlHashForm from "aml-hash-form";

// component
import PageBreadcrumb from "elements/System/PageBreadcrumb";
import IncidentFormFields from "elements/Incident/IncidentFormFields";
import { FwActionBar } from "elements/Incident/Primitives";
import {
  DraftBanner,
  DuplicateWarning,
  useIncidentDraft,
} from "elements/Incident/Resilience";
import { DuplicateHit, findRecentDuplicates } from "elements/Incident/duplicates";
import DispatchIntake, { ParsedDispatch } from "elements/Incident/DispatchIntake";
import { applyParsedDispatch } from "elements/Incident/applyParsed";
import AgDelete from "elements/System/AgDelete";
import AgStatus from "elements/System/AgStatus";
import Comments from "elements/System/Comments";
import { api, common } from "includes";

export default function AddIncident() {
  // const
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  let mode = searchParams.get("mode");
  let id = searchParams.get("id");

  // state
  const [process, setProcess] = useState<any>({ result: 100 });
  const [confirm, setConfirm] = useState<any>({ id: null, type: null });
  const [submit, setSubmit] = useState<any>("");
  const [resultList, setResultList] = useState<any>(null);
  const [finder, setFinder] = useState({
    locality: true,
    subLocality: true,
    respondingUnits: true,
    field1Value: true,
  });
  const [effectField, setEffectField] = useState<any>([]);
  const [locality, setLocality] = useState<any>({});
  const [locDefinition, setLocDefinition] = useState<any>({});
  const [facebookPages, setFacebookPages] = useState<any>([]);
  const [twitterPage, setTwitterPage] = useState<any>({});
  const [permission] = useState(common.getUserPermission());
  const [isReload, setIsReload] = useState<any>(null);
  const [formReady, setFormReady] = useState(false);
  const [authData] = useState<any>(common.getAuth());
  const [dupHits, setDupHits] = useState<DuplicateHit[] | null>(null);

  // form
  const formFields = {
    // title: { validate: ["req#Title is required"] },
    locality: {
      validate: ["objValReq#Locality is required!"],
      obj: "value",
    },
    subLocality: {
      validate: ["objValReq#Sub Locality is required!"],
      obj: "value",
    },
    respondingUnits: {
      validate: [],
      obj: "value",
    },
    latitude: { validate: ["req#Latitude is required"] },
    longitude: { validate: ["req#Longitude is required"] },
    address: { validate: [] },
    // description: { validate: [] },
    // box: { validate: [] },
    featured: { validate: [], value: false },
    sendPushNotification: { validate: [], value: false },
    postFacebook: { validate: [] },
    postTwitter: { validate: [] },
    field1Value: { validate: [] },
    field2Value: { validate: [] },
    field3Value: { validate: [] },
    field4Value: { validate: [] },
    field5Value: { validate: [] },
  };
  const {
    fields,
    errors,
    handleChange,
    handleSubmit,
    setValue,
    setMultiValue,
  } = AmlHashForm(formFields);

  // Drafts are per admin and per record; view mode never writes one.
  const draftBox = useIncidentDraft({
    userId: authData?._id || authData?.id || "anon",
    incidentId: id,
    enabled: mode !== "view" && formReady,
    fields,
    locality,
    locDefinition,
  });

  // Dispatch parsing fills the form in and stops there. Everything it wrote is
  // ordinary form state from here on — the admin edits it and submits by hand.
  const [needsCheck, setNeedsCheck] = useState<Record<string, boolean>>({});
  // Handed to the map so a parsed address is looked up and pinned, not just
  // written into the Street box. A parsed incident with no pin can't be
  // submitted at all — location is required — so filling the text without
  // dropping the pin left the admin to do the one step that actually gates
  // submission.
  const [searchAddress, setSearchAddress] = useState<string>("");

  const onParsed = async (parsed: ParsedDispatch) => {
    try {
      const applied = await applyParsedDispatch(parsed, "token");
      if (applied.locality) {
        setLocality(applied.locality);
        setLocDefinition(applied.locality);
        getLocFacebook(applied.locality.value);
      }
      setMultiValue(applied.values);
      // The pickers hold resolved {label,value} objects now, so the finder
      // lookups that would otherwise blank them have to be switched off.
      setFinder({
        locality: false,
        subLocality: false,
        respondingUnits: false,
        field1Value: false,
      });
      // The pin is a machine's best guess at a place, so it is always worth a
      // human glance — flagged whatever the parser's confidence in the text was.
      if (applied.values.address) {
        setSearchAddress(applied.values.address);
        applied.needsCheck.location = true;
      }
      setNeedsCheck(applied.needsCheck);
      common.notify("S", "Dispatch read — check the highlighted fields");
    } catch {
      common.notify("E", "Could not fill the form from that dispatch");
    }
  };

  const restoreDraft = () => {
    const d = draftBox.draft;
    if (!d) return;
    setMultiValue(d.fields || {});
    if (d.locality) setLocality(d.locality);
    if (d.locDefinition) setLocDefinition(d.locDefinition);
    // The finder flags exist so a select can resolve a bare id into an option
    // on load. A restored draft already holds full {label,value} objects, so
    // leaving them set would blank the very fields being restored.
    setFinder({
      locality: false,
      subLocality: false,
      respondingUnits: false,
      field1Value: false,
    });
    draftBox.dismiss();
  };

  // effect
  useEffect(() => {
    if (id) {
      getData();
    } else {
      processState("result", 200);
      setFormReady(true);
      // Only a new incident opts into push up front; an edit starts off and
      // waits for the admin. Scoped to the add branch so the default no longer
      // depends on getData's callback landing after this line.
      setMultiValue({ sendPushNotification: true });
    }
  }, []);

  useEffect(() => {
    if (isReload && id) {
      getData();
      setIsReload(false);
    }
  }, [isReload]);

  // useEffect(() => {
  //   if (id) {
  //     getData();
  //   } else {
  //     processState("result", 200);
  //   }
  // }, [mode]);

  useEffect(() => {
    if (effectField.length > 0) {
      setValue(effectField[0], effectField[1]);
      setFinder((prev) => ({ ...prev, [effectField[0]]: false }));
    }
  }, [effectField]);

  // api
  const getData = () => {
    processState("result", 100);
    let data: any = {
      url: `admin/incident/${id}`,
      method: "GET",
    };
    api.call(data, (res: any) => {
      processState("result", res.status);
      if (res.status === 200) {
        let result = res.data;
        setResultList(result);
        setMultiValue({
          locality: result.locality?._id || "",
          subLocality: result.subLocality?._id || "",
          respondingUnits: result?.respondingUnits || [],
          latitude: result.latitude || "",
          longitude: result.longitude || "",
          address: result.address || "",
          featured: result.featured || false,
          // Deliberately NOT result.sendPushNotification. Restoring the saved
          // value meant an incident created with push on re-notified every
          // follower each time it was saved again. Re-alerting is a decision
          // the admin makes per edit, so the toggle starts off and the
          // Delivery section stays open to make that visible.
          sendPushNotification: false,
          postFacebook: result.postFacebook || [],
          postTwitter: result.postTwitter || [],
          field1Value: result.field1Value
            ? { label: result.field1Value, value: result.field1Value }
            : null,
          field2Value: result.field2Value || "",
          field3Value: result.field3Value || "",
          field4Value: result.field4Value || "",
          field5Value: result.field5Value || "",
        });
        setFormReady(true);
      }
    });
  };

  const save = () => {
    let data = common.formPayload(fields, []);
    data.featured = fields.featured || false;
    data.sendPushNotification = fields.sendPushNotification || false;
    data.postFacebook =
      facebookPages?.filter((obj: any) => obj.checked === true) || [];
    data.postTwitter = twitterPage?.checked ? twitterPage : null;
    if (data.featured === false) {
      data.featuredImageUrl = null;
    }

    let param = {
      url: id ? `admin/incident/${id}` : `admin/incident`,
      method: id ? "PUT" : "POST",
      body: data,
    };

    api.call(param, (res: any) => {
      if (res.status === 200) {
        // The draft has become the record.
        draftBox.clear();
        common.notify(
          "S",
          `Incident ${id ? "updated" : "created"} successfully`
        );
        if (id) {
          navigate(`/incident/form?id=${id}&mode=view`);
          // navigate(`/incident/list`);
          processState("result", 100);
          getData();
        } else {
          navigate(`/incident/list`);
        }
      } else {
        common.notify("E", res?.data.error);
      }
      setSubmit("");
    });
  };

  const updateFeatured = () => {
    let param = {
      url: `admin/incident/${id}`,
      method: "PUT",
      body: { featuredImageUrl: null },
    };

    api.call(param, (res: any) => {
      if (res.status === 200) {
        setConfirm({ id: null, type: "" });
        let result: any = common.reParse(resultList);
        result.featuredImageUrl = null;
        setResultList(result);
      } else {
        common.notify("E", res?.data.error);
      }
      setSubmit("");
    });
  };

  const deleteLocality = () => {
    let data: any = {
      url: `admin/incident/${confirm.id}`,
      method: "DELETE",
    };

    api.call(data, (res: any) => {
      if (res.status === 200) {
        setConfirm({ id: null, type: "" });
        common.notify("S", "Incident deleted successfully");
        navigate(`/incident/list`);
      } else {
        common.notify("E", res.error);
      }
    });
  };

  const getLocFacebook = (id: any) => {
    let param = {
      url: `admin/locality/social/${id}`,
      method: "GET",
    };

    api.call(param, (res: any) => {
      if (res.status === 200) {
        setFacebookPages(res?.data?.facebook);
      }
      setSubmit("");
    });
  };

  // "Link a Facebook Page": hand the FB.login token to the server, which
  // exchanges it, reads the account's pages and stores permanent page tokens.
  // The browser can't do that step itself: those Graph calls need the app
  // secret. Tokens never come back here, only page names.
  const linkFacebook = (userAccessToken: string) => {
    if (!locality?.value) {
      common.notify("E", "Choose a locality before linking a Facebook page");
      return;
    }
    let param = {
      url: `admin/locality/${locality.value}/facebook/link`,
      method: "POST",
      body: { userAccessToken },
    };

    api.call(param, (res: any) => {
      if (res.status === 200) {
        const names = (res?.data?.pages || [])
          .map((p: any) => p.pageName)
          .join(", ");
        common.notify("S", `Facebook linked: ${names}`);
        getLocFacebook(locality.value);
      } else {
        common.notify(
          "E",
          res?.data?.error || "Linking the Facebook page failed. Try again."
        );
      }
    });
  };

  // events
  const onSubmit = (e: any) => {
    e.preventDefault();
    const isValid = handleSubmit();
    if (!isValid) return;

    // Only on create. An edit would match the record against itself, and a
    // duplicate that already exists is not news.
    if (id) {
      setSubmit("submit");
      save();
      return;
    }

    setSubmit("submit");
    findRecentDuplicates(
      {
        localityId: fields?.locality?.value || locality?.value,
        address: fields.address,
        box: fields.field3Value,
      },
      (hits) => {
        if (hits.length) {
          // Hand the decision back before anything is written.
          setSubmit("");
          setDupHits(hits);
        } else {
          save();
        }
      }
    );
  };

  const deleteCallBack = (isDelete: boolean) => {
    if (isDelete) {
      if (confirm.type === "incident") {
        deleteLocality();
      } else {
        updateFeatured();
      }
    } else {
      setConfirm({ id: null, type: "" });
    }
  };

  const handleCheckChange = (e: any, pageId: any) => {
    const newState = facebookPages.map((obj: any) =>
      obj.pageId == pageId ? { ...obj, checked: e.target.checked } : obj
    );
    setFacebookPages(newState);
  };

  const handleCheck2Change = (e: any) => {
    let twitter = common.reParse(twitterPage);
    twitter.checked = e.target.checked;
    setTwitterPage(twitter);
  };

  // callback
  const onAgCallBack = (status: any) => {
    getData();
  };

  // support
  const processState = (label: any, value: any) => {
    setProcess((prev: any) => ({
      ...prev,
      [label]: value,
    }));
  };

  // SubLocality query optimization
  const subLocalityQuery = useMemo(() => {
    return locality?.value
      ? `?offset=1&limit=1000&query={"locality":["${locality.value}"]}`
      : "";
  }, [locality?.value]);

  // Responding Units and Field1Option query optimization
  const respondingUnitsQuery = useMemo(() => {
    return locality?.value ? `?offset=1&limit=1000` : "";
  }, [locality?.value]);

  const fieldOptionQuery = useMemo(() => {
    return locality?.value ? `?offset=1&limit=1000` : "";
  }, [locality?.value]);

  const memoizedRespondingUnits = useMemo(() => {
    return Array.isArray(fields?.respondingUnits)
      ? fields.respondingUnits
        .map((u: any) => {
          if (typeof u === "string") return { label: u, value: u };
          if (u?.value && u?.label) return u;
          if (u?.unitName) return { label: u.unitName, value: u.unitName };
          return null;
        })
        .filter((u: any, i: any, arr: any) => u?.value && arr.findIndex((a: any) => a?.value === u?.value) === i)
      : [];
  }, [fields?.respondingUnits]);

  return (
    <Fragment>
      <PageBreadcrumb
        breadCrumbItems={[
          { label: "Incident", path: "/incident/list" },
          { label: "Incident Form", path: "", active: true },
        ]}
        title={"Incident Form"}
      />
      {process.result != 200 ? (
        <AgStatus
          process={process.result}
          message={{
            100: "Loading... Please wait!",
            404: "Something went wrong! Please Refresh Again",
          }}
          button={{ 400: "Refresh", 404: "Refresh" }}
          size="l"
          onAgCallBack={onAgCallBack}
        />
      ) : (
        <Fragment>
          {mode != "view" ? (
            <div className="fw-incident">
              <div className="fw-incident__head">
                <Link
                  to="/incident/list"
                  className="fw-incident__back"
                  aria-label="Back to incidents"
                >
                  <i className="ri-arrow-left-line" />
                </Link>
                <h1>{id ? "Update incident" : "New incident"}</h1>
              </div>

              <DraftBanner
                draft={draftBox.draft}
                isEdit={Boolean(id)}
                onRestore={restoreDraft}
                onDiscard={draftBox.dismiss}
              />

              {!id && <DispatchIntake onParsed={onParsed} />}

              <IncidentFormFields
                needsCheck={needsCheck}
                searchAddress={searchAddress}
                isEdit={Boolean(id)}
                authRole={authData.role}
                fields={fields}
                errors={errors}
                handleChange={handleChange}
                setValue={setValue}
                setMultiValue={setMultiValue}
                formReady={formReady}
                finder={finder}
                setEffectField={setEffectField}
                locality={locality}
                locDefinition={locDefinition}
                onLocalitySelect={(data: any) => {
                  if (!data) return;
                  setLocDefinition(data);
                  setEffectField(["locality", data]);
                  setLocality(data);

                  setTwitterPage({
                    twitterPage: data.twitterPage || null,
                    checked: false,
                  });

                  getLocFacebook(data.value);
                }}
                subLocalityQuery={subLocalityQuery}
                respondingUnitsQuery={respondingUnitsQuery}
                fieldOptionQuery={fieldOptionQuery}
                memoizedRespondingUnits={memoizedRespondingUnits}
                facebookPages={facebookPages}
                twitterPage={twitterPage}
                onFacebookPagesLinked={linkFacebook}
                onFacebookPageCheck={(pageId: any, checked: boolean) =>
                  handleCheckChange({ target: { checked } }, pageId)
                }
                onTwitterCheck={(checked: boolean) =>
                  handleCheck2Change({ target: { checked } })
                }
              />

              <FwActionBar
                submitting={submit === "submit"}
                submitLabel={id ? "Save update" : "Submit incident"}
                onSubmit={onSubmit}
                onCancel={() => navigate("/incident/list")}
              />
            </div>
          ) : (
            <Fragment>
              <Row>
                <Col xl={12} lg={12}>
                  <Card>
                    <Card.Body className="py-1 px-1 border-bottom border-light">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="float-start">
                          <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip id="">Back</Tooltip>}
                          >
                            <Link
                              to="/incident/list"
                              className="text-reset fs-20 p-1 d-inline-block"
                            >
                              <i
                                className="ri-arrow-left-s-line pe-2"
                                data-bs-container="#tooltips-container"
                                data-bs-toggle="tooltip"
                              />
                            </Link>
                          </OverlayTrigger>
                          <label className="form-check-label fs-16">
                            Incident Information
                          </label>
                        </div>
                        {permission.includes("inu") && (
                          <div id="tooltips-container d-flex align-items-center">
                            <div className="d-flex flex-wrap gap-2">
                              <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip id="">Edit</Tooltip>}
                              >
                                <Button
                                  className="btn btn-light"
                                  onClick={() => {
                                    getData();
                                    navigate(
                                      `/incident/form?id=${id}&mode=edit`
                                    );
                                  }}
                                >
                                  <i
                                    className="ri-edit-2-line"
                                    data-bs-container="#tooltips-container"
                                    data-bs-toggle="tooltip"
                                  />
                                </Button>
                              </OverlayTrigger>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col>
                  <Card>
                    <Card.Body>
                      <Form>
                        <Row>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Locality</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList?.locality?.name || "-"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Sub Locality</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList?.subLocality?.name || "-"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Responding Units</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList?.respondingUnits?.length
                                  ? resultList.respondingUnits.join(", ")
                                  : "-"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Send PushNotification</Form.Label>
                              <p className="text-muted fs-14">
                                {Boolean(resultList.sendPushNotification) ==
                                  true
                                  ? "Y"
                                  : "N"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Featured</Form.Label>
                              <p className="text-muted fs-14">
                                {Boolean(resultList.featured) == true
                                  ? "Y"
                                  : "N"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Address</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList.address || "-"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Latitude</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList.latitude || "-"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Longtitude</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList.longitude || "-"}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Created By</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList.createdBy?.firstName}
                                {"-"}
                                {resultList.createdBy?.lastName
                                  ? resultList.createdBy?.lastName
                                  : ""}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Modified By</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList.modifiedBy?.firstName}
                                {"-"}
                                {resultList.modifiedBy?.lastName
                                  ? resultList.modifiedBy?.lastName
                                  : ""}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Version Number</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList.versionNumber || 0}
                              </p>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>Featured Image</Form.Label>
                              {resultList.featuredImageUrl ? (
                                <div className="d-flex align-items-center">
                                  <Image
                                    src={resultList.featuredImageUrl}
                                    alt="post-img"
                                    className="rounded me-2"
                                    width="60"
                                    height="60"
                                  />
                                  <Button
                                    variant="danger"
                                    onClick={() =>
                                      setConfirm({ id: id, type: "featured" })
                                    }
                                  >
                                    Remove Feature
                                  </Button>
                                </div>
                              ) : (
                                <div>-</div>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label>View Count</Form.Label>
                              <p className="text-muted fs-14">
                                {resultList?.views || 0}
                              </p>
                            </Form.Group>
                          </Col>
                          {permission.includes("ind") && (
                            <Col md={4}>
                              <Button
                                variant="danger"
                                size="sm"
                                className="me-1 d-felx align-items-center"
                                onClick={() =>
                                  setConfirm({ id: id, type: "incident" })
                                }
                              >
                                <i className="ri-delete-bin-line fs-16" />
                                <span className="ms-1">
                                  Permanently Delete Incident
                                </span>
                              </Button>
                            </Col>
                          )}
                        </Row>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Comments
                    incidentId={id}
                    featured={resultList?.featured}
                    refresh={() => setIsReload(true)}
                  />
                </Col>
              </Row>
            </Fragment>
          )}
        </Fragment>
      )}
      <DuplicateWarning
        hits={dupHits}
        onCancel={() => setDupHits(null)}
        onProceed={() => {
          setDupHits(null);
          setSubmit("submit");
          save();
        }}
      />

      {/* Delete confirmation */}
      {confirm.id && (
        <AgDelete type="delete" onAgDeleteCallBack={deleteCallBack} />
      )}
    </Fragment>
  );
}
